import { useReactiveVar } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import { WarningType } from 'components/common/MenuItem';
import AccountContext from 'context/AccountContext';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import useGetAvailableAnprPaymentOption, {
  AnprPaymentOption,
} from 'hooks/useGetAvailableAnprPaymentOption';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useUiStore } from 'stores/uiStore';
import { featureCountryCode } from 'states/persistInStorage';
import { PaymentType } from 'types/generatedSchemaTypes';
import { AccountNavProps } from 'types/navigation';
import { truncateText } from 'utils/commonUtils';
import {
  isMobilePayEnabledForCountry,
  showFeatureFlag,
} from 'utils/featureFlagUtils';
import { getPaymentMethodsIndicator } from 'utils/indicatorUtils';
import {
  getCardLogo,
  getCardMaskedLastNums,
  getDefaultPaymentOption,
  getPaymentMethodLogo,
  getWalletCardMenuPresentation,
} from 'utils/paymentUtils';
import AutomaticVehicleSelectToggleItem from './AutomaticVehicleSelectToggleItem';
import { MenuItemWrapped, MenuSection } from './MenuComponents';

const VehiclePaymentMenu = () => {
  const { navigate } = useNavigation<AccountNavProps>();
  const { t } = useTranslation();
  const indicators = useUiStore((state) => state.tabBarIndicators);
  const {
    flags: { show_benefits, enable_mobilepay_by_country },
  } = useContext(FeatureFlagsContext);
  const showBenefits = showFeatureFlag(show_benefits);
  const {
    customer: {
      paymentCards,
      paymentOptions: customerPaymentOptions,
      vehicles,
      contactEmail,
    },
  } = useContext(AccountContext);
  const appCountryCode = useReactiveVar(featureCountryCode);
  const showMobilePay = isMobilePayEnabledForCountry(
    enable_mobilepay_by_country,
    appCountryCode,
    contactEmail
  );
  const vehiclesWithAnprCount = vehicles?.filter(
    (vehicle) => vehicle.cameraRecognition?.enabled
  ).length;

  const defaultPaymentOption = getDefaultPaymentOption(
    customerPaymentOptions,
    paymentCards
  );

  function getDefaultPaymentMethodText() {
    const method = defaultPaymentOption?.paymentOption.method;
    const effectiveMethod =
      method === 'MOBILEPAY' && !showMobilePay ? null : method;

    // Fall back to card.isDefault for accounts without paymentOptions yet
    const defaultCard =
      defaultPaymentOption?.card ??
      paymentCards?.find((c) => c?.isDefault) ??
      undefined;
    // A stored wallet default card presents as wallet brand + custom name;
    // plain cards keep the "type name ****1234" string.
    const walletPresentation = getWalletCardMenuPresentation(defaultCard, t);
    const defaultCardText = defaultCard
      ? (walletPresentation?.subtitle ??
        `${defaultCard.type} ${truncateText(defaultCard.description ?? '', 15)} ${getCardMaskedLastNums(defaultCard)}`)
      : undefined;
    const defaultPaymentMethodText =
      effectiveMethod === 'MOBILEPAY'
        ? t('paymentMethods.mobilePay')
        : defaultCardText;
    return {
      defaultCard,
      defaultPaymentMethodText,
      effectiveMethod,
      walletIconName: walletPresentation?.iconName,
    };
  }

  const {
    defaultCard,
    defaultPaymentMethodText,
    effectiveMethod,
    walletIconName,
  } = getDefaultPaymentMethodText();
  const anprPaymentOption = useGetAvailableAnprPaymentOption();
  const paymentMethodsIndicator = useMemo(
    () => getPaymentMethodsIndicator(indicators),
    [indicators]
  );

  const getPaymentWarning = (
    paymentOption: AnprPaymentOption,
    anprVehicleCount: number | undefined
  ) => {
    if (paymentOption === 'NO_OPTIONS') {
      return { type: WarningType.ERROR, amount: anprVehicleCount ?? 0 };
    } else if (paymentOption === 'COMPANY_POOL') {
      return { type: WarningType.WARNING, amount: anprVehicleCount ?? 0 };
    }
    return undefined;
  };

  return (
    <MenuSection headline={t('account.vehiclePayment')}>
      <AutomaticVehicleSelectToggleItem />
      {showBenefits && (
        <MenuItemWrapped
          menuLabel={t('account.benefits')}
          iconName="Benefits"
          testID="benefits-link"
          onPress={() => navigate('benefits')}
        />
      )}
      <MenuItemWrapped
        menuLabel={t('account.myVehicles')}
        iconName="Car-Outlined"
        testID="your-vehicles-link"
        onPress={() => navigate('myVehicles')}
        warning={getPaymentWarning(anprPaymentOption, vehiclesWithAnprCount)}
      />
      <MenuItemWrapped
        menuLabel={t('account.paymentMethods')}
        iconName={
          walletIconName ??
          getPaymentMethodLogo(
            effectiveMethod as PaymentType,
            defaultCard?.type
          )?.logoName ??
          getCardLogo(defaultCard?.type ?? null)
        }
        indicatorType={paymentMethodsIndicator?.indicatorType}
        indicatorNumber={paymentMethodsIndicator?.indicatorNumber}
        testID="credit-cards-link"
        onPress={() => navigate('myPaymentMethods')}
        noBorder
        subtitle={defaultPaymentMethodText}
      />
    </MenuSection>
  );
};

export default VehiclePaymentMenu;
